import { LoginButton } from '../routes/landing/components/login'
import { useAuthQuery as useQuery } from './hooks/useAuthQuery';
import { useConvexAuth } from 'convex/react';
import { useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { Badge } from './badge';
import { BaseContext } from './base-context';
import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { MainRoutes } from './models';
import { useStableQuery } from './hooks/useStableQuery';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';
import { Button } from '../catalyst-components/button';
import { AcornIcon } from './components/AcornIcon';
import { useAnimatedCount } from './hooks/useAnimatedCount';
import { FAQHelpIcon } from './components/FAQHelpIcon';
import { Tooltip } from './components/Tooltip';
import { BoarMascot } from './components/BoarMascot';

const getLocalDateString = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const useCountdownToLocalMidnight = (active: boolean) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!active) return;

    const calc = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const diff = Math.max(0, nextMidnight.getTime() - now.getTime());
      const totalSec = Math.floor(diff / 1000);
      return {
        hours: Math.floor(totalSec / 3600),
        minutes: Math.floor((totalSec % 3600) / 60),
        seconds: totalSec % 60,
      };
    };

    setTimeLeft(calc());
    const id = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(id);
  }, [active]);

  return timeLeft;
};

export const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useConvexAuth();
  const { isSidebarShown, convexUserId } = useContext(BaseContext);
  const onboardingCompleted = useStableQuery(api.user_metadata.getOnboardingCompleted, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
  const navigate = useNavigate();

  // Acorn data
  const localDate = getLocalDateString();
  const acornData = useQuery(
    api.acorns.getUserAcornsWithAllowance,
    convexUserId ? { user_id: convexUserId as Id<"users">, localDate } : "skip"
  );
  const claimAllowance = useMutation(api.acorns.claimDailyAllowance);

  const { display: animatedBalance, direction: balanceDirection } = useAnimatedCount(acornData?.balance ?? 0);
  const canClaim = acornData?.can_claim_allowance ?? true;
  const countdown = useCountdownToLocalMidnight(!canClaim);
  const countdownStr = `${String(countdown.hours).padStart(2, '0')}:${String(countdown.minutes).padStart(2, '0')}`;
  const claimTooltip = canClaim ? t('header.claimTooltipReady') : t('header.claimTooltipCooldown');
  const claimLabel = t('header.claimAllowance', { allowance: acornData?.daily_allowance });
  const allowanceAmount = t('header.allowanceAmount', { allowance: acornData?.daily_allowance });

  const handleClaimAllowance = async () => {
    if (!convexUserId) return;
    try {
      await claimAllowance({ user_id: convexUserId as Id<"users">, localDate });
    } catch (error) {
    }
  };

  // Show full header bar when authenticated and onboarded (and sidebar not shown)
  if (isAuthenticated && onboardingCompleted && !isSidebarShown) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 backdrop-blur-sm">
        <div className="max-w-screen-xl mx-auto px-2 sm:px-6 py-2 sm:py-3 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <BoarMascot className="h-12 sm:h-14" />
          </div>

          {/* Right Side - Acorns and Actions */}
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Daily Claim Button — visible when claimable */}
            {acornData && canClaim && (
              <Tooltip content={claimTooltip} placement="bottom">
                <Button
                  onClick={handleClaimAllowance}
                  color="primary"
                  className="gap-1.5 sm:gap-2 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 tabular-nums"
                >
                  <span className="hidden sm:inline">{claimLabel}</span>
                  <span className="sm:hidden text-sm">{allowanceAmount}</span>
                  <AcornIcon size={16} className="text-primary-foreground" />
                </Button>
              </Tooltip>
            )}

            {/* Cooldown timer — compact, shown when not claimable */}
            {acornData && !canClaim && (
              <Tooltip content={claimTooltip} placement="bottom">
                <div className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-muted/40 rounded-full text-muted-foreground">
                  <span className="text-xs tabular-nums">{countdownStr}</span>
                  <AcornIcon size={12} />
                </div>
              </Tooltip>
            )}

            {/* Acorn Balance */}
            {acornData && (
              <Tooltip content={t('header.balanceTooltip')} placement="bottom">
                <div className={`flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-card border-2 rounded-full shadow-sm transition-colors duration-300 ${
                  balanceDirection === 'up' ? 'border-primary bg-primary/5' :
                  balanceDirection === 'down' ? 'border-gray-400 bg-gray-50/50' :
                  'border-primary/30'
                }`}>
                  <AcornIcon size={20} className={`transition-colors duration-300 sm:w-[26px] sm:h-[26px] ${
                    balanceDirection === 'up' ? 'text-primary' :
                    balanceDirection === 'down' ? 'text-gray-400' :
                    'text-primary'
                  }`} />
                  <span className={`text-base sm:text-lg font-semibold transition-colors duration-300 tabular-nums ${
                    balanceDirection === 'up' ? 'text-primary' :
                    balanceDirection === 'down' ? 'text-gray-500' :
                    'text-foreground'
                  }`}>{animatedBalance}</span>
                </div>
              </Tooltip>
            )}

            {/* FAQ Help */}
            <FAQHelpIcon size="md" tooltipText={t('header.faq')} />

            {/* Chat Button */}
            <button
              onClick={() => navigate(MainRoutes.MyChat)}
              className="p-1.5 sm:p-2 rounded-full hover:bg-secondary transition-all"
              title={t('header.chat')}
            >
              <ChatBubbleLeftRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-muted-foreground hover:text-foreground" />
            </button>

            {/* Profile Badge */}
            <Badge />
          </div>
        </div>
      </header>
    );
  }

  // Minimal header for unauthenticated or onboarding users
  return (
    <header className="fixed top-4 right-4 z-50">
      <nav aria-label="Global" className="flex items-center gap-3">
        <div className="flex items-center gap-3">
            { !isAuthenticated ? <LoginButton/> : ''}
            { isAuthenticated && !isSidebarShown ? <Badge/> : ''}
        </div>
      </nav>
    </header>
  )
}
